import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import {
  ArrowBack,
  ArrowForward,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
  Delete,
  Edit,
  ExpandLess,
} from 'Components/Atoms/Icons';
import React, { useEffect, useMemo, useState } from 'react';
import { EditReservation, Guest } from 'types/reservations';
import GuestDropdown from '../../GuestDropdown';
// import GuestDropdownV02 from '../../GuestDropdownV02';
import TextField from 'Components/Atoms/TextField';
import AttributeDropdown from '../../AttributeDropdown';
import Typography from 'Components/Atoms/Typography';
import useGuestAutoComplete from 'CustomHooks/useGuestAutoComplete';
import Checkbox from 'Components/Atoms/Checkbox';
import usePhoneNumberValidator from 'CustomHooks/usePhoneNumberValidator';
import useSiaContext from 'CustomHooks/useSiaContext';
import CallListItem from 'Components/Organisms/SiaModal/Components/CallListItem';
import { ExtendedCall } from 'CustomHooks/useCalls';
import server from 'utils/server';
import useRestaurant from 'CustomHooks/useRestaurant';
import {
  canReceiveSms,
  getAdditionalInternalFee,
  normalizeEmail,
  toCurrencyString,
  toEuropeanDate,
} from 'utils/helper';
import { Trans, useTranslation } from 'react-i18next';
import IconButton from 'Components/Atoms/IconButton';
import CustomElements from './CustomElements';
import StaffProtectionOption from '../../NoShowProtection/StaffProtectionOption';
import CommentModal from 'Components/Molecules/CommentModal';
import { SiaStateLiveCall } from 'types/sia';
import {
  getTicketPartialPaymentFraction,
  getTicketPaymentSummary,
  getTicketValidTillMinutes,
} from 'utils/ticketSession';

type SecondStepProps = {
  reservation: EditReservation;
  onReservationChange: React.Dispatch<
    React.SetStateAction<EditReservation | null>
  >;
  onSave: () => Promise<void>;
};

type reservationFields =
  | 'guest'
  | 'phone'
  | 'email'
  | 'comment'
  | 'hostComment'
  | 'guestComment'
  | 'attr'
  | 'page'
  | 'sendEmail'
  | 'sendSms'
  | 'company'
  | 'preferredLanguage'
  | 'ticketSendVia'
  | 'ticketValidTillMinutes'
  | 'ticketValidTillValue'
  | 'ticketValidTillUnit'
  | 'ticketPartialPayment'
  | 'ticketPartialPaymentMode'
  | 'ticketPartialPaymentAmount'
  | 'ticketCanEdit'
  | 'ticketCouponCode'
  | 'ticketCoupon'
  | 'ticketVoucherCode'
  | 'ticketVoucher';

type newValue =
  | string
  | string[]
  | number
  | undefined
  | Guest
  | boolean
  | EditReservation['ticketCoupon']
  | EditReservation['ticketVoucher']
  | EditReservation['ticketValidTillUnit']
  | EditReservation['ticketPartialPaymentMode'];

const EMPTY_GUEST: Guest = {
  name: '',
  guestId: '',
  phone: '',
  email: '',
  hostComment: '',
  comment: '',
  attr: [],
  phoneNumbers: [],
};

const useNoShowFee = (reservation: EditReservation) => {
  const { restaurantId } = useRestaurant();

  const [noShowFee, setNoShowFee] = useState<{
    skip: boolean;
    client_secret?: string;
    customer_id?: string;
    restaurantId?: string;
    reservationId?: string;
    publishable_key?: string;
    link?: string;
    disclaimer?: Record<string, string>;
    innerDisclaimer?: string;
  } | null>(null);

  useEffect(() => {
    server
      .get<{
        skip: boolean;
        client_secret?: string;
        customer_id?: string;
        restaurantId?: string;
        reservationId?: string;
        publishable_key?: string;
        link?: string;
        disclaimer?: Record<string, string>;
        innerDisclaimer?: string;
      }>(
        `/v03/reservations/checkNoShow/${restaurantId}/${reservation.reservationId}`
      )
      .then(({ data }) => {
        setNoShowFee(data);
      })
      .catch((err) => setNoShowFee(null));
  }, [reservation.reservationId, restaurantId]);

  return noShowFee;
};

const SecondStep = ({
  onReservationChange,
  reservation,
  onSave,
}: SecondStepProps) => {
  const { t } = useTranslation('reservations');
  const phoneValidator = usePhoneNumberValidator((number) =>
    handleChange(number, 'phone')
  );
  const [durationSettingsOpen, setDurationSettingsOpen] = useState(false);
  const [sendViaSettingsOpen, setSendViaSettingsOpen] = useState(false);
  const [upfrontPaymentSettingsOpen, setUpfrontPaymentSettingsOpen] =
    useState(false);

  const {
    restaurantId,
    reservationSettings,
    experimentalFlags,
    globalFlags,
    hasSiaV02,
    siaState,
    apps,
  } = useRestaurant();

  const { hasSia, calls } = useSiaContext();

  const sendEmailDefault =
    !experimentalFlags?.data?.confirmationEmailOffByDefault;

  const handleChange = (newValue: newValue, type: reservationFields) => {
    const newReservation: EditReservation = { ...reservation };
    if (type === 'page') {
      newReservation[type] = newValue as any;
    } else if (type.startsWith('ticket')) {
      (newReservation as any)[type] = newValue;
    } else if (type === 'guest') {
      const nextGuest = newValue ? { ...(newValue as Guest) } : (newValue as Guest);

      if (nextGuest?.email) {
        nextGuest.sendEmail = sendEmailDefault;
      }

      newReservation.guest = nextGuest;

      if ((newValue as any)?.phone !== reservation.guest?.phone) {
        phoneValidator.validatePhoneNumber((newValue as any)?.phone);
      }
    } else {
      const newGuest: Guest = reservation.guest
        ? { ...reservation.guest }
        : EMPTY_GUEST;
      newGuest[type] = newValue as Exclude<keyof Guest, newValue>;

      if (type === 'email' && newValue) {
        newGuest.sendEmail = sendEmailDefault;
      }

      if (type === 'phone' && !canReceiveSms(newValue as string)) {
        newGuest.sendSms = false;
      }

      newReservation.guest = { ...newGuest };
    }
    onReservationChange(newReservation);
  };

  const handleTicketPatch = (patch: Partial<EditReservation>) => {
    onReservationChange({
      ...reservation,
      ...patch,
    });
  };

  const activeCalls = useMemo(() => {
    return calls.filter((c) => c.createdAt > Date.now() - 20 * 60000);
  }, [calls]);

  const liveCalls = useMemo(
    () => Object.values(siaState.data?.live_calls ?? {}),
    [siaState.data?.live_calls]
  );

  const handleCallClick = (call: ExtendedCall) =>
    onReservationChange((old) => {
      if (!old) return null;

      if (call.guest) {
        const { name, email, phone, attr = [], ...guest } = call.guest;

        return {
          ...old,
          guest: {
            ...(guest as any),
            name,
            email,
            phone,
            attr,
            comment: old.guest?.comment || '',
            id: call.guest.guestId,
          },
        };
      } else {
        return {
          ...old,
          guest: {
            name: '',
            email: '',
            phone: call.callerId,
            attr: [],
            comment: old.guest?.comment || '',
            hostComment: '',
          },
        };
      }
    });

  const handleLiveCallClick = (call: SiaStateLiveCall) => {
    phoneValidator.validatePhoneNumber(call.callerId);
    onReservationChange((old) => {
      if (!old) return null;
      const guest: Guest = {
        ...(old.guest ?? EMPTY_GUEST),
        name: call.name ?? old.guest?.name ?? '',
        phone: call.callerId,
      };

      return {
        ...old,
        guest,
      };
    });
  };

  const noShowFee = useNoShowFee(reservation);
  const noShowSmsPhone = reservation.guest?.phone?.trim() ?? '';
  const noShowSmsSupportedCountry =
    noShowSmsPhone.startsWith('+49') || noShowSmsPhone.startsWith('+43');
  const showNoShowSmsOption =
    !!noShowFee?.customer_id && !reservation.addToWaitinglist;
  const disableNoShowSms = !noShowSmsSupportedCountry;
  const showNoShowSmsHint =
    showNoShowSmsOption && !!noShowSmsPhone && !noShowSmsSupportedCountry;
  const smsEnabled =
    apps.includes('sms_bundle' as any) || apps.includes('sms_confirmation' as any);
  const smsConsentPhoneOk = canReceiveSms(reservation.guest?.phone);
  const hasTicketSession =
    reservation.ticketMode === 'pay_at_restaurant' ||
    reservation.ticketMode === 'payment_link';
  const hasMollieProtection =
    apps.includes('noShowMollie') &&
    !!reservation.noShowProtectionTemplate &&
    !reservation.addToWaitinglist &&
    !hasTicketSession;
  const [couponStatus, setCouponStatus] = useState<{
    message: string;
    error: boolean;
    loading: boolean;
  }>({ message: '', error: false, loading: false });
  const [voucherStatus, setVoucherStatus] = useState<{
    message: string;
    error: boolean;
    loading: boolean;
  }>({ message: '', error: false, loading: false });
  const ticketSummary = useMemo(() => {
    if (!hasTicketSession || !reservation.event) return null;

    const fullSummary = getTicketPaymentSummary({
      event: reservation.event,
      guests: reservation.guests ?? 0,
      ticket: reservation.ticket,
      coupon: reservation.ticketCoupon,
      voucher: reservation.ticketVoucher,
      partialPayment: 1,
      includeServiceFee: false,
    });
    const partialPayment =
      reservation.ticketMode === 'payment_link'
        ? getTicketPartialPaymentFraction(reservation, fullSummary.ticketTotal)
        : 1;

    return getTicketPaymentSummary({
      event: reservation.event,
      guests: reservation.guests ?? 0,
      ticket: reservation.ticket,
      coupon: reservation.ticketCoupon,
      voucher: reservation.ticketVoucher,
      partialPayment,
      includeServiceFee: reservation.ticketMode === 'payment_link',
    });
  }, [
    hasTicketSession,
    reservation.event,
    reservation.guests,
    reservation.ticket,
    reservation.ticketCoupon,
    reservation.ticketMode,
    reservation.ticketPartialPaymentAmount,
    reservation.ticketPartialPaymentMode,
    reservation.ticketPartialPayment,
    reservation.ticketVoucher,
  ]);
  const internalFeeConfig =
    experimentalFlags?.data?.additionalInternalGastronautFee;
  const hideServiceFee = !!(ticketSummary && internalFeeConfig?.value);
  const internalGastronautFee =
    ticketSummary && internalFeeConfig?.value
      ? getAdditionalInternalFee(ticketSummary.ticketTotal, internalFeeConfig)
      : 0;
  const validTillValue = reservation.ticketValidTillValue ?? 2;
  const validTillUnit = reservation.ticketValidTillUnit ?? 'hours';
  const validTillUnitLabel =
    validTillUnit === 'minutes'
      ? t(validTillValue === 1 ? 'Minute' : 'Minutes')
      : validTillUnit === 'hours'
        ? t(validTillValue === 1 ? 'Hour' : 'Hours')
        : t(validTillValue === 1 ? 'Day' : 'Days');
  const validTillSummary = `${validTillValue} ${validTillUnitLabel}`;
  const upfrontPaymentMode =
    reservation.ticketPartialPaymentMode ?? 'percentage';
  const upfrontPaymentSummary =
    upfrontPaymentMode === 'amount'
      ? toCurrencyString(
          reservation.ticketPartialPaymentAmount ??
            ticketSummary?.requiredOnlineBase ??
            ticketSummary?.ticketTotal ??
            0,
          '€',
          false,
          1
        )
      : `${Math.round((reservation.ticketPartialPayment ?? 1) * 100)}%`;
  const ticketSendVia = reservation.ticketSendVia ?? 'email';
  const sendViaSummary =
    ticketSendVia === 'sms'
      ? t('SMS')
      : ticketSendVia === 'none'
        ? t('Do not send automatically')
        : t('Email');
  const guestCanEditTicket = reservation.ticketCanEdit ?? true;

  const validateCoupon = async () => {
    const code = reservation.ticketCouponCode?.trim();

    if (!code) {
      handleChange(null, 'ticketCoupon');
      setCouponStatus({ message: '', error: false, loading: false });
      return;
    }

    if (!restaurantId || !reservation.reservationId) {
      setCouponStatus({
        message: 'Die Reservierung muss zuerst angelegt werden.',
        error: true,
        loading: false,
      });
      return;
    }

    setCouponStatus({ message: '', error: false, loading: true });

    try {
      const { data } = await server.get<{
        valid: boolean;
        coupon?: NonNullable<EditReservation['ticketCoupon']>;
        reason?: string;
      }>(
        `/v03/reservations/${restaurantId}/res/${reservation.reservationId}/coupon/${code}`
      );

      if (!data.valid || !data.coupon) {
        handleChange(null, 'ticketCoupon');
        setCouponStatus({
          message: data.reason || 'Coupon ist nicht gültig.',
          error: true,
          loading: false,
        });
        return;
      }

      handleChange(data.coupon, 'ticketCoupon');
      setCouponStatus({
        message:
          data.coupon.title ||
          data.coupon.description ||
          'Coupon wurde angewendet.',
        error: false,
        loading: false,
      });
    } catch (error: any) {
      handleChange(null, 'ticketCoupon');
      setCouponStatus({
        message: error?.response?.data?.message || error.message,
        error: true,
        loading: false,
      });
    }
  };

  const validateVoucher = async () => {
    const code = reservation.ticketVoucherCode?.trim();

    if (!code) {
      handleChange(null, 'ticketVoucher');
      setVoucherStatus({ message: '', error: false, loading: false });
      return;
    }

    if (!restaurantId || !reservation.event) {
      setVoucherStatus({
        message: 'Die Reservierung muss zuerst angelegt werden.',
        error: true,
        loading: false,
      });
      return;
    }

    setVoucherStatus({ message: '', error: false, loading: true });

    try {
      const { data } = await server.get<
        NonNullable<EditReservation['ticketVoucher']>
      >(
        `/v03/vouchersV02/${restaurantId}/validate/${code}?purpose=event_${reservation.event.id}_${reservation.date}`
      );

      if (!data.valid) {
        handleChange(null, 'ticketVoucher');
        setVoucherStatus({
          message: data.message || 'Gutschein ist nicht gültig.',
          error: true,
          loading: false,
        });
        return;
      }

      handleChange(data, 'ticketVoucher');
      setVoucherStatus({
        message: data.message || 'Gutschein wurde angewendet.',
        error: false,
        loading: false,
      });
    } catch (error: any) {
      handleChange(null, 'ticketVoucher');
      setVoucherStatus({
        message:
          error?.response?.data?.message ||
          error?.response?.data?.reason ||
          error.message,
        error: true,
        loading: false,
      });
    }
  };

  useEffect(() => {
    if (showNoShowSmsOption && disableNoShowSms && reservation.sendNoShowSMS) {
      onReservationChange((old) =>
        !old ? null : { ...old, sendNoShowSMS: false }
      );
    }
  }, [
    disableNoShowSms,
    onReservationChange,
    reservation.sendNoShowSMS,
    showNoShowSmsOption,
  ]);

  useEffect(() => {
    if (!smsEnabled && reservation.ticketSendVia === 'sms') {
      onReservationChange((old) =>
        !old ? null : { ...old, ticketSendVia: 'email' },
      );
    }
  }, [onReservationChange, reservation.ticketSendVia, smsEnabled]);

  return (
    <Box className="new-reservation-sidebar">
      <Button
        startIcon={() => <ArrowBack style={{ marginRight: 8 }} />}
        fullWidth
        onClick={() => handleChange(0, 'page')}
        variant="outlined"
        style={{
          backgroundColor: 'var(--color-bg)',
        }}
      >
        Go Back
      </Button>
      {!!reservation.addToWaitinglist && (
        <Box style={{ color: 'var(--color-warning)', margin: '12px 0px' }}>
          <Typography
            variant="text-3"
            translation="reservations"
            block
            textAlign="center"
            color="inherit"
          >
            This is creating a Waitinglist Entry not a Reservation!
          </Typography>
        </Box>
      )}
      <Box className="second-step-field">
        <Box>
          <Box className="flex space-between" style={{ marginBottom: 4 }}>
            <Typography variant="text-3" translation="reservations" block>
              Date
            </Typography>
            <Typography
              variant="text-2"
              color="subdued"
              translation="reservations"
            >
              {toEuropeanDate(reservation.date)}{' '}
              {reservation.timeSlot?.time ?? reservation.time}
            </Typography>
          </Box>
          <Box className="flex space-between" style={{ marginBottom: 12 }}>
            <Typography variant="text-3" translation="reservations" block>
              <Trans i18nKey="reservations_Guests">Guests</Trans>{' '}
              {!reservation.addToWaitinglist && '& Tables'}
            </Typography>
            <Typography
              variant="text-2"
              color="subdued"
              translation="reservations"
            >
              {reservation.guests} PAX{' '}
              {(!reservation.addToWaitinglist &&
                `• ${reservation.tables?.join(', ')}`) ||
                ''}
            </Typography>
          </Box>
        </Box>

        {!hasSiaV02 && hasSia && !!activeCalls?.length && (
          <details className="sia-calls-picker">
            <summary>Anrufe von SIA ({activeCalls.length})</summary>
            <Box
              style={{
                maxHeight: 98,
                overflow: activeCalls.length > 1 ? 'scroll' : 'hidden',
                marginBottom: 20,
              }}
            >
              {activeCalls.map((call) => (
                <CallListItem
                  call={call}
                  key={call.id}
                  noStatus
                  active={
                    (call.guest?.phone || call.callerId) ===
                    reservation?.guest?.phone
                  }
                  onClick={() => handleCallClick(call)}
                />
              ))}
            </Box>
          </details>
        )}
        {hasSiaV02 && !!liveCalls.length && (
          <details className="sia-calls-picker">
            <summary>Anrufe von SIA ({liveCalls.length})</summary>
            <Box className="sia-live-calls-list">
              {liveCalls.map((call) => (
                <Button
                  key={call.id}
                  size="sm"
                  variant="transparent"
                  className={
                    call.callerId === reservation?.guest?.phone
                      ? 'sia-live-call-button active'
                      : 'sia-live-call-button'
                  }
                  onClick={() => handleLiveCallClick(call)}
                >
                  {call.name
                    ? `${call.name} (${call.callerId})`
                    : call.callerId}
                </Button>
              ))}
            </Box>
          </details>
        )}
        <GuestDropdown
          value={reservation.guest}
          onChange={(guest) => handleChange(guest, 'guest')}
        />
      </Box>
      {/* Show remaining fields only after a guest is selected or "New Guest" is clicked */}
      {!!(reservation.guest?.id || reservation.guest?.createNew) && (<>
      <Box className="second-step-field">
        <TextField
          value={reservation.guest?.phone || ''}
          onChange={(e) => handleChange(e.target.value, 'phone')}
          fullWidth
          required={!!reservationSettings?.phoneNumberMandatory}
          type="phone"
          defaultCountry={experimentalFlags?.data?.phoneDefaultCountry || 'de'}
          label="Phone Number"
          labelTranslation="reservations"
          {...phoneValidator.hook}
        />
        {smsEnabled && !hasMollieProtection && !!reservation.guest?.phone && (
          <>
            <Checkbox
              checked={!!reservation.guest.sendSms}
              onChange={(_, sendSms) => handleChange(sendSms, 'sendSms')}
              label="Guest agreed to receiving an SMS"
              translation="reservations"
              disabled={!reservation.guest?.sendSms && !smsConsentPhoneOk}
            />
            {!smsConsentPhoneOk && (
              <Typography variant="text-4" color="subdued" translation="reservations">
                SMS is only available for German and Austrian mobile numbers.
              </Typography>
            )}
          </>
        )}

        {!!reservation.guest?.phoneNumbers &&
          reservation.guest.phoneNumbers.map((number, i) => (
            <Box className="space-between" key={i}>
              <TextField
                value={number || ''}
                onChange={(e) =>
                  onReservationChange({
                    ...reservation,
                    guest: {
                      ...(reservation.guest ?? EMPTY_GUEST),
                      phoneNumbers:
                        reservation.guest?.phoneNumbers?.map((g, i1) =>
                          i === i1 ? e.target.value : g
                        ) ?? [],
                    },
                  })
                }
                fullWidth
                required
                type="phone"
                defaultCountry={
                  experimentalFlags?.data?.phoneDefaultCountry || 'de'
                }
                label={'Alternative Telefonnummer ' + (i + 1)}
                labelTranslation="reservations"
              />
              <IconButton
                size="small"
                color="subdued"
                onClick={() => {
                  onReservationChange({
                    ...reservation,
                    guest: {
                      ...(reservation.guest ?? EMPTY_GUEST),
                      phoneNumbers:
                        reservation.guest?.phoneNumbers?.filter(
                          (x, i2) => i2 !== i
                        ) ?? [],
                    },
                  });
                }}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}

        <Button
          variant="transparent"
          color="primary"
          fullWidth
          onClick={() =>
            onReservationChange({
              ...reservation,
              guest: {
                ...(reservation.guest ?? EMPTY_GUEST),
                phoneNumbers: [...(reservation.guest?.phoneNumbers ?? []), ''],
              },
            })
          }
        >
          Neue Alternative Telefonnr.
        </Button>

        {showNoShowSmsOption && (
          <>
            <Checkbox
              checked={!!reservation.sendNoShowSMS}
              onChange={(_, sendNoShowSMS) =>
                onReservationChange((old) =>
                  !old ? null : { ...old, sendNoShowSMS }
                )
              }
              label="No Show SMS versenden"
              translation="reservations"
              style={{ marginLeft: -12 }}
              disabled={disableNoShowSms}
            />
            {showNoShowSmsHint && (
              <Box style={{ marginLeft: 26, marginBottom: 6 }}>
                <Typography
                  variant="text-4"
                  color="subdued"
                  translation="reservations"
                >
                  No-Show SMS is only available for German and Austrian numbers.
                </Typography>
              </Box>
            )}
            <Box style={{ maxHeight: 120, overflow: 'scroll' }}>
              <Typography variant="text-4" color="subdued">
                {noShowFee.innerDisclaimer ||
                  noShowFee.disclaimer?.['de'] ||
                  ''}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <Box className="second-step-field">
        <TextField
          value={reservation.guest?.email || ''}
          onChange={(e) => handleChange(normalizeEmail(e.target.value), 'email')}
          fullWidth
          label="Email"
          required={!!reservationSettings?.emailMandatory}
          labelTranslation="reservations"
        />
        {!!reservation.guest?.email && (
          <Checkbox
            checked={!!reservation.guest.sendEmail}
            onChange={(_, sendEmail) => handleChange(sendEmail, 'sendEmail')}
            label="Guest agreed to receiving an Email"
            translation="reservations"
          />
        )}
      </Box>

      {!hasTicketSession && (
        <StaffProtectionOption
          reservation={reservation}
          onReservationChange={onReservationChange}
        />
      )}

      {hasTicketSession && (
        <Box
          className="second-step-field"
          style={{
            borderTop: '1px solid var(--color-border)',
            paddingTop: 12,
          }}
        >
          <Typography variant="text-3" block translation="reservations">
            Ticket payment
          </Typography>
          {reservation.ticketMode === 'payment_link' && (
            <>
              <Box className="ticket-compact-setting">
                <button
                  type="button"
                  className="ticket-compact-setting-summary"
                  onClick={() => setSendViaSettingsOpen((open) => !open)}
                >
                  <Box className="ticket-compact-setting-copy">
                    <Typography
                      variant="text-4"
                      color="subdued"
                      block
                      translation="reservations"
                    >
                      Send overview link via
                    </Typography>
                    <Typography variant="text-3">{sendViaSummary}</Typography>
                  </Box>
                  <span className="ticket-compact-setting-icon">
                    {sendViaSettingsOpen ? <ExpandLess /> : <Edit />}
                  </span>
                </button>
                {sendViaSettingsOpen && (
                  <Box className="ticket-compact-setting-controls">
                    <Box className="ticket-radio-group">
                      <Checkbox
                        radio
                        checked={ticketSendVia === 'email'}
                        onChange={() => handleChange('email', 'ticketSendVia')}
                        label="Email"
                        translation="reservations"
                        style={{ marginLeft: -12 }}
                      />
                      <Checkbox
                        radio
                        checked={ticketSendVia === 'sms'}
                        disabled={!smsEnabled}
                        onChange={() => handleChange('sms', 'ticketSendVia')}
                        label="SMS"
                        translation="reservations"
                        style={{ marginLeft: -12 }}
                      />
                      <Checkbox
                        radio
                        checked={ticketSendVia === 'none'}
                        onChange={() => handleChange('none', 'ticketSendVia')}
                        label="Do not send automatically"
                        translation="reservations"
                        style={{ marginLeft: -12 }}
                      />
                    </Box>
                    {!smsEnabled && (
                      <Typography
                        variant="text-4"
                        color="subdued"
                        block
                        translation="reservations"
                      >
                        SMS is only available when the SMS app is active.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Box className="ticket-compact-setting">
                <button
                  type="button"
                  className="ticket-compact-setting-summary"
                  onClick={() => setDurationSettingsOpen((open) => !open)}
                >
                  <Box className="ticket-compact-setting-copy">
                    <Typography
                      variant="text-4"
                      color="subdued"
                      block
                      translation="reservations"
                    >
                      Link valid for
                    </Typography>
                    <Typography variant="text-3">{validTillSummary}</Typography>
                  </Box>
                  <span className="ticket-compact-setting-icon">
                    {durationSettingsOpen ? <ExpandLess /> : <Edit />}
                  </span>
                </button>
                {durationSettingsOpen && (
                  <Box className="ticket-compact-setting-controls">
                    <TextField
                      value={validTillValue}
                      onChange={(e) => {
                        const value = Math.max(1, Number(e.target.value || 2));
                        const unit = reservation.ticketValidTillUnit ?? 'hours';

                        handleTicketPatch({
                          ticketValidTillValue: value,
                          ticketValidTillUnit: unit,
                          ticketValidTillMinutes: getTicketValidTillMinutes(
                            value,
                            unit
                          ),
                        });
                      }}
                      type="number"
                      fullWidth
                      label="Link valid for"
                      labelTranslation="reservations"
                    />
                    <Box className="ticket-radio-group horizontal">
                      {(['minutes', 'hours', 'days'] as const).map((unit) => (
                        <Checkbox
                          key={unit}
                          radio
                          checked={validTillUnit === unit}
                          onChange={() =>
                            handleTicketPatch({
                              ticketValidTillValue: validTillValue,
                              ticketValidTillUnit: unit,
                              ticketValidTillMinutes: getTicketValidTillMinutes(
                                validTillValue,
                                unit
                              ),
                            })
                          }
                          label={
                            unit === 'minutes'
                              ? 'Minutes'
                              : unit === 'hours'
                                ? 'Hours'
                                : 'Days'
                          }
                          translation="reservations"
                          style={{ marginLeft: -12 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
              <Box className="ticket-compact-setting">
                <button
                  type="button"
                  className="ticket-compact-setting-summary"
                  onClick={() =>
                    setUpfrontPaymentSettingsOpen((open) => !open)
                  }
                >
                  <Box className="ticket-compact-setting-copy">
                    <Typography
                      variant="text-4"
                      color="subdued"
                      block
                      translation="reservations"
                    >
                      Payment required
                    </Typography>
                    <Typography variant="text-3">
                      {upfrontPaymentSummary}
                    </Typography>
                  </Box>
                  <span className="ticket-compact-setting-icon">
                    {upfrontPaymentSettingsOpen ? <ExpandLess /> : <Edit />}
                  </span>
                </button>
                {upfrontPaymentSettingsOpen && (
                  <Box className="ticket-compact-setting-controls">
                    <Box className="ticket-radio-group horizontal">
                      <Checkbox
                        radio
                        checked={upfrontPaymentMode === 'percentage'}
                        onChange={() =>
                          handleTicketPatch({
                            ticketPartialPaymentMode: 'percentage',
                            ticketPartialPayment:
                              reservation.ticketPartialPayment ?? 1,
                          })
                        }
                        label="Percentage"
                        translation="reservations"
                        style={{ marginLeft: -12 }}
                      />
                      <Checkbox
                        radio
                        checked={upfrontPaymentMode === 'amount'}
                        onChange={() =>
                          handleTicketPatch({
                            ticketPartialPaymentMode: 'amount',
                            ticketPartialPaymentAmount:
                              ticketSummary?.requiredOnlineBase ??
                              ticketSummary?.ticketTotal ??
                              0,
                          })
                        }
                        label="Amount"
                        translation="reservations"
                        style={{ marginLeft: -12 }}
                      />
                    </Box>
                    {upfrontPaymentMode === 'percentage' ? (
                      <TextField
                        value={Math.round(
                          (reservation.ticketPartialPayment ?? 1) * 100
                        )}
                        onChange={(e) =>
                          handleChange(
                            Math.min(
                              Math.max(Number(e.target.value || 100), 1),
                              100
                            ) / 100,
                            'ticketPartialPayment'
                          )
                        }
                        type="number"
                        fullWidth
                        label="Payment percentage"
                        labelTranslation="reservations"
                      />
                    ) : (
                      <TextField
                        value={reservation.ticketPartialPaymentAmount ?? ''}
                        onChange={(e) =>
                          handleChange(
                            Math.max(Number(e.target.value || 0), 0),
                            'ticketPartialPaymentAmount'
                          )
                        }
                        type="number"
                        fullWidth
                        label="Payment amount"
                        labelTranslation="reservations"
                      />
                    )}
                  </Box>
                )}
              </Box>
              <Box className="ticket-compact-setting">
                <button
                  type="button"
                  aria-pressed={guestCanEditTicket}
                  className="ticket-compact-setting-summary"
                  onClick={() =>
                    handleChange(!guestCanEditTicket, 'ticketCanEdit')
                  }
                >
                  <Box className="ticket-compact-setting-copy">
                    <Typography
                      variant="text-4"
                      color="subdued"
                      block
                      translation="reservations"
                    >
                      Guest can edit ticket options before paying
                    </Typography>
                    <Typography variant="text-3" translation="reservations">
                      {guestCanEditTicket ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </Box>
                  <span
                    className={
                      'ticket-compact-setting-icon' +
                      (guestCanEditTicket ? ' active' : '')
                    }
                  >
                    {guestCanEditTicket ? (
                      <CheckBoxIcon />
                    ) : (
                      <CheckBoxOutlineBlank />
                    )}
                  </span>
                </button>
              </Box>
            </>
          )}
          <Box className="ticket-code-row">
            <TextField
              value={reservation.ticketCouponCode || ''}
              onChange={(e) => {
                handleTicketPatch({
                  ticketCouponCode: e.target.value,
                  ticketCoupon: null,
                });
                setCouponStatus({ message: '', error: false, loading: false });
              }}
              onBlur={validateCoupon}
              fullWidth
              label="Coupon code"
              labelTranslation="reservations"
            />
            <IconButton
              size="small"
              className="ticket-code-action"
              aria-label="Coupon anwenden"
              title="Coupon anwenden"
              disabled={couponStatus.loading}
              onClick={validateCoupon}
            >
              <ArrowForward />
            </IconButton>
          </Box>
          {!!couponStatus.message && (
            <Typography
              variant="text-4"
              block
              style={{
                color: couponStatus.error
                  ? 'var(--color-critical)'
                  : 'var(--color-success)',
              }}
            >
              {couponStatus.message}
            </Typography>
          )}
          <Box className="ticket-code-row">
            <TextField
              value={reservation.ticketVoucherCode || ''}
              onChange={(e) => {
                handleTicketPatch({
                  ticketVoucherCode: e.target.value,
                  ticketVoucher: null,
                });
                setVoucherStatus({ message: '', error: false, loading: false });
              }}
              onBlur={validateVoucher}
              fullWidth
              label="Voucher code"
              labelTranslation="reservations"
            />
            <IconButton
              size="small"
              className="ticket-code-action"
              aria-label="Gutschein anwenden"
              title="Gutschein anwenden"
              disabled={voucherStatus.loading}
              onClick={validateVoucher}
            >
              <ArrowForward />
            </IconButton>
          </Box>
          {!!voucherStatus.message && (
            <Typography
              variant="text-4"
              block
              style={{
                color: voucherStatus.error
                  ? 'var(--color-critical)'
                  : 'var(--color-success)',
              }}
            >
              {voucherStatus.message}
            </Typography>
          )}
          {!!ticketSummary && (
            <Box className="ticket-payment-summary">
              <Typography
                variant="text-4"
                color="subdued"
                block
                translation="reservations"
              >
                Payment overview
              </Typography>
              <Box className="ticket-payment-summary-row">
                <Typography variant="text-4" translation="reservations">
                  Ticket total
                </Typography>
                <Typography variant="text-4">
                  {toCurrencyString(ticketSummary.ticketTotal, '€', false, 1)}
                </Typography>
              </Box>
              {reservation.ticketMode === 'payment_link' && (
                <Box className="ticket-payment-summary-row">
                  <Typography variant="text-4" translation="reservations">
                    Required online payment
                  </Typography>
                  <Typography variant="text-4">
                    {toCurrencyString(
                      ticketSummary.requiredOnlineBase,
                      '€',
                      false,
                      1
                    )}
                  </Typography>
                </Box>
              )}
              {!!ticketSummary.discount && (
                <Box className="ticket-payment-summary-row ticket-payment-summary-discount">
                  <Typography variant="text-4" translation="reservations">
                    Coupon
                  </Typography>
                  <Typography variant="text-4">
                    -{' '}
                    {toCurrencyString(ticketSummary.discount, '€', false, 1)}
                  </Typography>
                </Box>
              )}
              {!!ticketSummary.voucherAmount && (
                <Box className="ticket-payment-summary-row ticket-payment-summary-discount">
                  <Typography variant="text-4" translation="reservations">
                    Voucher
                  </Typography>
                  <Typography variant="text-4">
                    -{' '}
                    {toCurrencyString(
                      ticketSummary.voucherAmount,
                      '€',
                      false,
                      1
                    )}
                  </Typography>
                </Box>
              )}
              {!!ticketSummary.serviceFee && !hideServiceFee && (
                <Box className="ticket-payment-summary-row">
                  <Typography variant="text-4" translation="reservations">
                    Service fee
                  </Typography>
                  <Typography variant="text-4">
                    +{' '}
                    {toCurrencyString(ticketSummary.serviceFee, '€', false, 1)}
                  </Typography>
                </Box>
              )}
              {internalGastronautFee > 0 && (
                <Box className="ticket-payment-summary-row">
                  <Typography variant="text-4" translation="reservations">
                    Gastronaut commission
                  </Typography>
                  <Typography variant="text-4">
                    - {toCurrencyString(internalGastronautFee, '€', false, 1)}
                  </Typography>
                </Box>
              )}
              <Box className="ticket-payment-summary-row ticket-payment-summary-total">
                <Typography variant="text-3" translation="reservations">
                  {reservation.ticketMode === 'payment_link'
                    ? 'Guest pays online'
                    : 'Guest pays at restaurant'}
                </Typography>
                <Typography variant="text-3">
                  {toCurrencyString(
                    Math.max(
                      (reservation.ticketMode === 'payment_link'
                        ? ticketSummary.guestPaysOnline
                        : ticketSummary.ticketTotal -
                          ticketSummary.discount -
                          ticketSummary.voucherAmount) -
                        internalGastronautFee -
                        (hideServiceFee ? ticketSummary.serviceFee : 0),
                      0
                    ),
                    '€',
                    false,
                    1
                  )}
                </Typography>
              </Box>
              {reservation.ticketMode === 'payment_link' &&
                !!ticketSummary.remainingAtRestaurant && (
                  <Box className="ticket-payment-summary-row">
                    <Typography variant="text-4" translation="reservations">
                      Remaining at restaurant
                    </Typography>
                    <Typography variant="text-4">
                      {toCurrencyString(
                        ticketSummary.remainingAtRestaurant,
                        '€',
                        false,
                        1
                      )}
                    </Typography>
                  </Box>
                )}
            </Box>
          )}
        </Box>
      )}

      <Box className="second-step-field">
        <TextField
          value={reservation.guest?.company || ''}
          onChange={(e) => handleChange(e.target.value, 'company')}
          fullWidth
          label="Company"
          labelTranslation="reservations"
        />
      </Box>

      {!globalFlags?.data?.deactivateGuestComment && (
        <Box className="second-step-field">
          <CommentModal
            value={reservation.guest?.comment || ''}
            onChange={(e) => handleChange(e, 'comment')}
            fullWidth
            label="Comment (Guest)"
            labelTranslation="reservations"
          />
        </Box>
      )}
      <Box className="second-step-field">
        <CommentModal
          value={reservation.guest?.hostComment || ''}
          onChange={(e) => handleChange(e, 'hostComment')}
          fullWidth
          label="Comment (Host)"
          labelTranslation="reservations"
        />
      </Box>
      <Box className="second-step-field">
        <CommentModal
          value={reservation.guest?.guestComment || ''}
          onChange={(e) => handleChange(e, 'guestComment')}
          fullWidth
          label="Langzeit Kommentar"
          labelTranslation="reservations"
        />
      </Box>
      <Box className="second-step-field">
        <Typography variant="text-3" translation="reservations">
          Attributes
        </Typography>
        <AttributeDropdown
          value={reservation.guest?.attr || []}
          onChange={(result) => handleChange(result, 'attr')}
        />
      </Box>
      <Checkbox
        checked={(reservation.guest?.preferredLanguage ?? 'de') !== 'de'}
        onChange={(e, checked) =>
          handleChange(checked ? 'en' : 'de', 'preferredLanguage')
        }
        label="Gast spricht Englisch"
        translation="reservations"
        style={{ marginLeft: -12 }}
      />
      <CustomElements
        id={reservation.reservationId ?? ''}
        customData={reservation.customData ?? {}}
        onCustomDataUpdate={(customData, complete) =>
          // Both the initial load and an upload resolve later, a spread of the
          // rendered reservation would drop newer input or reopen a saved one.
          // A reload after going back keeps the values typed before.
          onReservationChange((old) =>
            !!old && old.reservationId === reservation.reservationId
              ? {
                  ...old,
                  customData: complete
                    ? { ...customData, ...(old.customData ?? {}) }
                    : { ...(old.customData ?? {}), ...customData },
                }
              : old
          )
        }
      />
      </>)}
    </Box>
  );
};

export default SecondStep;
